import { PaymentProviderCapability, PaymentsAccount } from './PaymentsAccount'
import { Address } from './Address'
import { BookingReason } from './BookingReason'
import { Event } from './Event'
import { Question, QuestionTypeNaturalOrder } from './Question'
import { OpeningHours } from './OpeningHours'
import { shouldShowVoucherVenueName, voucherDisplayName } from '../../../../shared/domain/voucher-display-name'

export class Venue {

    public showLargerPartiesUnreservable: boolean
    public usesReasonsAsDurations: boolean

    constructor(
        public id: string,
        public venueSlug: string | null,
        public displayName: string,
        public phoneNumber: string | null,
        public emailAddress: string | null,
        public address: Address,
        public bookingInterval: number,
        public minLargePartySize: number | null,
        public largePartyMessage: string | null,
        public noBookingSlotAvailableMessage: string | null,
        public shortPreBookingWindowMessage: string | null,
        public dogFriendlyNoAvailabilityMessage: string | null,
        public bookingsPausedNoAvailabilityMessage: string | null,
        public venueClosedNoAvailabilityMessage: string | null,
        public currencyCode: string | null,
        public organisationId: string,
        public businessId: string,
        public entitlements: string[],
        public paymentsAccount: PaymentsAccount | null,
        public cancellationChargeAmount: number | null,
        public cancellationChargeCutOffHours: number | null,
        public depositAmount: number | null,
        public depositRefundCutOffDays: number | null,
        public depositRefundPolicy: string | null,
        public phoneNumberRequired: boolean,
        public primaryColour: string | null,
        public pinnedEventId: string | null,
        public dogFriendlyQuestionEnabled: boolean,
        public wheelchairAccessibleQuestionEnabled: boolean,
        public pramAccessibleQuestionEnabled: boolean,
        public highchairFriendlyQuestionEnabled: boolean,
        public customerCanChooseArea: boolean,
        /**
         * @deprecated Retained for backwards-compatible API inspection only.
         * It must not determine area choice or allocation behaviour.
         */
        public areaBookingOrder: string[],
        public bookingsClosedUntilDate: Date | null,
        public bookingPolicy: string | null,
        public largePartyBookingPolicy: string | null,
        public largePartyBookingPolicyMinimumPartySize: number | null,
        public hidesClosedBookingSlots: boolean,
        public enquiriesEnabled: boolean,
        public waitlistsEnabled: boolean,
        public voucherLogoImageId: string | null,
        public reasons: BookingReason[],
        public questions: Question[],
        public events: Event[],
        public description: string | null = null,
        public promotionalImageIds: string[] = [],
        public openingHours: OpeningHours | null = null,
        public containsCustomersRequiringDeposit: boolean = false,
        public businessDisplayName: string | null = null,
        public privacyPolicy: string | null = null,
        public preorderMenuId: string | null = null,
        public paymentsAccountAssignments?: Partial<Record<PaymentProviderCapability, PaymentsAccount>>
    ) {
        const showLargerPartiesUnreservableIds = [
            '8025acaf-1c0d-4695-86d8-65804d890a4b' // Bier Halle
        ]
        const venueIdsUsingReasonsAsDurations = [
            '173ffecb-3449-4667-b4fd-10670c7a13ff', // Law New Venue Local
            '21a650df-2db6-49d3-8eff-416e086e541c', // Cocktail Geeks Staging
            '7d8e1058-df9d-42a1-bb55-dddf2b460998', // Victoria Bar Production
        ]
        this.showLargerPartiesUnreservable = showLargerPartiesUnreservableIds.includes(this.id)
        this.usesReasonsAsDurations = venueIdsUsingReasonsAsDurations.includes(this.id)
    }

    paymentsAccountForCapability(capability: PaymentProviderCapability): PaymentsAccount | null {
        const account = this.paymentsAccountAssignments === undefined
            ? this.paymentsAccount : this.paymentsAccountAssignments[capability]
        return account?.supports(capability) ? account : null
    }

    supportsCardSetupWithBookingPayment(): boolean {
        const payment = this.paymentsAccountForCapability('DepositPayments')
        const cancellation = this.paymentsAccountForCapability('CancellationChargePayments')
        return !!payment && !!cancellation && payment.id === cancellation.id && payment.gateway === cancellation.gateway
            && ['stripe', 'ryftpay', 'tablesense-test'].includes(payment.gateway)
    }

    voucherDisplayName(): string {
        return voucherDisplayName(this.id, this.displayName)
    }

    shouldShowVoucherStorefrontAddressCard(): boolean {
        return shouldShowVoucherVenueName(this.id)
    }

    reasonWithId(id: string): BookingReason | null {
        return this.reasons.find(reason => reason.id === id) || null
    }

    supportsModalExperience(landingPageId?: string): boolean {
        if (landingPageId !== undefined) {
            return false
        }
        // If Ugly Duck, always open landing page instead of the modal flow
        if (this.id === '6b244949-8053-4dce-b40c-dc1c2a16c266') {
            return false
        }
        // If using the venue storefront, always open landing page instead of the modal flow
        if (this.shouldUseVenueStorefrontExperience()) {
            return false
        }
        const providerNeedsDomainForApplePay = (['DepositPayments', 'CancellationChargePayments'] as const).some(capability => {
            const account = this.paymentsAccountForCapability(capability)
            return account?.usesRyftPay() && account.applePayEnabled
        })
        return !providerNeedsDomainForApplePay
    }

    shouldUseVenueStorefrontExperience(): boolean {
        return this.entitlements.includes('venue-storefront')
    }

    shouldUseVenueStorefrontChoiceGrid(): boolean {
        const venueIdsUsingChoiceGrid: string[] = [
            '851567d6-6c27-4488-bce9-3908c4c6687b', // Law local
            '5a0585fe-be28-40d2-83f7-8a5f2b993a50', // Law Golf and Things Local
            '6ddf7b8a-86ce-4ca6-aeb9-332b8bc2e263', // Golf and Things Staging
            '4a315b73-ad25-44c2-a21b-1d94b100c1c4', // Safari & Co
            '6f7ca398-0f78-4d8b-a4e1-88cf4d443bb4', // The Paddock
            'dda04640-7452-4887-9359-c1dccd574255', // Fired Arts
        ]
        return venueIdsUsingChoiceGrid.includes(this.id)
    }

    customerCanChooseAreaFor(reason: BookingReason | null): boolean {
        return !reason?.areaBookingOrder?.length && this.customerCanChooseArea
    }

    reasonsUsedOnDate(dateTime: Date): BookingReason[] {
        return this.reasons
            .filter(r => r.usedOnDate(dateTime))
            .sort((a, b) => a.displayOrder - b.displayOrder)
    }

    reasonsUsedOnDateTime(dateTime: Date): BookingReason[] {
        return this.reasons
            .filter(r => r.usedOnDateTime(dateTime))
            .sort((a, b) => a.displayOrder - b.displayOrder)
    }

    eventWithId(id: string): Event | null {
        return this.events.find(event => event.id === id) || null
    }

    hasAccessibilityQuestions(): boolean {
        return this.dogFriendlyQuestionEnabled
            || this.wheelchairAccessibleQuestionEnabled
            || this.pramAccessibleQuestionEnabled
            || this.highchairFriendlyQuestionEnabled
    }

    bookingPolicies(): string[] {
        return this.bookingPolicy ? [this.bookingPolicy] : []
    }

    pinnedEvent(): Event | null {
        return this.events.find(event => event.id === this.pinnedEventId) || null
    }

    questionsToAskWhenBookingForConfiguration(
        reasonId: string | null,
        eventId: string | null
    ): Question[] {
        return this.questions
            .filter(question => question.active)
            .filter(question => {
                if (eventId !== null) {
                    const event = this.eventWithId(eventId)
                    return event?.questionIds.includes(question.id) || false
                }
                if (reasonId !== null) {
                    return question.reasonIds.includes(reasonId) || question.reasonIds.length === 0
                }
                return question.reasonIds.length === 0
            })
            .sort((a, b) => {
                const aIndex = QuestionTypeNaturalOrder.indexOf(a.type)
                const bIndex = QuestionTypeNaturalOrder.indexOf(b.type)
                return aIndex - bIndex
            })
    }

    cancellationChargeCutOffHoursForConfiguration(selectedEventId: string | null): number | null {
        if (selectedEventId === null) {
            return this.cancellationChargeCutOffHours
        }
        const event = this.eventWithId(selectedEventId)
        return event?.cancellationChargeCutOffHours || null
    }

    depositRefundCutOffDaysForConfiguration(selectedEventId: string | null): number | null {
        if (selectedEventId === null) {
            return this.depositRefundCutOffDays
        }
        const event = this.eventWithId(selectedEventId)
        return event?.depositRefundCutOffDays || null
    }
}
